import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forIn from 'lodash/forIn';
import get from 'lodash/get';
import identity from 'lodash/identity';
import intersection from 'lodash/intersection';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import mapKeys from 'lodash/mapKeys';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import moment from 'moment';
/* CONSTANTS */
import * as constants from 'vd-constants';
import {
  EVENT_FIELD_LINE_CROSS_DOWN,
  EVENT_FIELD_LINE_CROSS_IN,
  EVENT_FIELD_LINE_CROSS_LEFT,
  EVENT_FIELD_LINE_CROSS_OUT,
  EVENT_FIELD_LINE_CROSS_RIGHT,
  EVENT_FIELD_LINE_CROSS_TOTAL,
  EVENT_FIELD_LINE_CROSS_UP,
  EVENT_FIELD_STORE_OCCUPANCY_COUNT,
  PERCENT_THRESHOLD_VIEW_SETTINGS,
  RANKING_WIDGETS_VIEW_SETTINGS,
  TABLEVIEW_STANDARD_FIELDS,
  WIDGETS_VIEW_SETTINGS,
} from 'vd-constants';
/* COMPONENTS */
import SMSIcon from '@mdi/svg/svg/cellphone-message.svg?react';
import EmailIcon from '@mdi/svg/svg/email.svg?react';
import { MDIIcon, Public } from '@solinkcorp/solink-components';
/* ACTIONS */
import { setFilter } from 'actions/filterActions';
/* STORES */
import { LocationStore } from 'stores/LocationStore';
/* UTILS */
import { USER_TYPE_ADMIN } from '@solinkcorp/authentication';
import { featureStandard } from '@solinkcorp/event-standard';
import {
  ALL_EVENTS_REPORT,
  ALL_LOCATIONS,
  calcTotalsAndDifference as calcTotalsAndDifferenceUtil,
  CommonWidgetUtils,
  DEFAULT_PERCENT_THRESHOLD_VIEW,
  FIELD_NAME_EMPTY,
  getBucketData,
  getDayOfWeekNumber,
  getRangeDetailOptions,
  getRankingWidgetDataFieldName,
  getRankingWidgetYAxisName,
  GRAPH_TYPE,
  HISTOGRAM_RANGE,
  HistogramApiHelper,
  HOUR_REPORT_WIDGET_DEFAULT_VIEW,
  IS_RANKING_DEFAULT_SUMMARY_VIEW,
  PERCENT_THRESHOLD_VIEW_BAR,
  PERCENT_THRESHOLD_VIEW_LINE,
  RANGE_DETAIL_DEFAULT_VALUE_BY_RANGE,
  RANK_BY_NAME,
  RANKING_WIDGET_DEFAULT_FIELD_NAME,
  RANKING_WIDGET_DEFAULT_RANK_BY,
  RANKING_WIDGET_DEFAULT_RANK_BY_LOCATION,
  RANKING_WIDGET_DEFAULT_VIEW,
  RANKING_WIDGET_LEGACY_DATA_PATHS,
  REPORT_WIDGET_DEFAULT_VIEW,
  sanitizeWidget,
  SORT_ASC,
  SORT_DESC,
  THRESHOLD_EMPTY_VALUE,
  THRESHOLD_RANGE_MAP,
  THRESHOLD_RANKING_MAX_ITEMS,
  ThresholdType,
  WIDGET_DATA_CATEGORY_OTHER,
  WIDGET_DATA_PATH_BOOKMARKS_COUNT,
  WIDGET_DATA_PATH_EVENTS_COUNT,
  WIDGET_DATA_PATH_MOTIONS_COUNT,
  WIDGET_DATA_STATISTIC_AVG,
  WIDGET_DATA_STATISTIC_COUNT,
  WIDGET_DATA_STATISTIC_PERCENTAGE,
  WIDGET_DATA_STATISTIC_RATIO,
  WIDGET_DATA_STATISTIC_SUM,
  WIDGET_DATA_UNIT_TYPE_QUANTITY,
  WIDGET_DEFINITIONS,
  WIDGET_INHERIT_VALUE,
  WIDGET_LEGACY_DATA_FIELDS,
  WIDGET_LEGACY_DATA_PATH_TOTAL_AMOUNT,
  WIDGET_LEGACY_DATA_PATH_TRANSACTIONS_COUNT,
  WIDGET_TAG_THRESHOLDS,
  WIDGET_TYPE_CAMERA_GROUP,
  WIDGET_TYPE_EVENT_TICKER,
  WIDGET_TYPE_LOCATION_RANKING,
  WIDGET_TYPE_RANKING,
  WIDGET_TYPE_RANKING_THRESHOLD,
  WIDGET_TYPE_REPORT,
  WIDGET_TYPE_REPORT_THRESHOLD,
  WIDGET_VIEW_DETAIL,
  WIDGET_VIEW_SUMMARY,
} from '@solinkcorp/vd-common';
import { StorageAPI } from 'clients';
import { flatLocationTags } from 'components/SettingsPage/LocationManagement/locationTagUtils';
import AuthService from 'models/AuthService';
import CommonUtil from 'utils/CommonUtil';
import { getAvailableFeatures } from 'utils/FeatureStandardUtils';
import FeaturesUtil from 'utils/FeaturesUtil';
import HierarchyUtil from 'utils/HierarchyUtil';
import LocaleUtil from 'utils/LocaleUtil';
import { mutableDeepClone } from 'utils/utils';
import { LONG_RANKING_SIZE } from 'utils/WidgetUtil';
import { canCalPercentage, getWidgetTagsOptions } from './helperFunctions';
import { determineDeviceIds, isWidgetInDigest } from './homeUtils';

const INITIAL_THRESHOLDS = [
  { type: ThresholdType.ABSOLUTE, aboveValue: 0, belowValue: THRESHOLD_EMPTY_VALUE },
];

// type SettingsOptionFields
const REPORT_SETTINGS_OPTION_FIELDS = {
  fields: [
    'dataCategory',
    'dataPaths',
    'dataPathRatioWith',
    'dataStatistic',
    'graphType',
    'locationIds',
    'range',
    'rangeDetails',
    'rangeRollingAlternates', // alternate values to `range`
  ],
  nonInheritableFields: [
    'dataCategory',
    'dataPaths',
    'dataPathRatioWith',
    'dataStatistic',
    'graphType',
    'reportTemplateIds',
    'widgetMetadata',
  ],
};

const RANKING_SETTINGS_OPTION_FIELDS = {
  fields: [
    'dataCategory',
    'dataStatistic',
    'dataPathRatioWith',
    'fieldName',
    'locationIds',
    'order',
    'range',
    'rangeRollingAlternates', // alternate values to `range`
  ],
  nonInheritableFields: [
    'dataCategory',
    'dataStatistic',
    'dataPathRatioWith',
    'fieldName',
    'reportTemplateIds',
    'order',
    'widgetMetadata',
  ],
};

const CAMERA_GROUP_SETTINGS_OPTION_FIELDS = {
  fields: ['cameraGroupId'],
  nonInheritableFields: ['cameraGroupId', 'widgetMetadata'],
};

const TICKER_SETTINGS_OPTION_FIELDS = {
  fields: ['dataCategory', 'dataPaths', 'dataStatistic', 'locationIds'],
  nonInheritableFields: ['dataCategory', 'dataPaths', 'dataStatistic', 'reportTemplateIds'],
};

const THRESHOLD_SETTINGS_OPTION_FIELDS = {
  fields: [
    'locationIds',
    'range',
    'rangeRollingAlternates', // alternate values to `range`
    'rangeDetails',
  ],
  nonInheritableFields: ['locationIds', 'range', 'rangeRollingAlternates', 'rangeDetails'],
};

/**
 * Which fields from the widget settings form should get options.
 * This is to be used as arguments to HomeHelper.getSelectOptions().
 * type {[K in WidgetType]: SettingsOptionFields]}
 */
const WIDGET_SETTINGS_OPTION_FIELDS = {
  [WIDGET_TYPE_REPORT]: REPORT_SETTINGS_OPTION_FIELDS,
  [WIDGET_TYPE_RANKING]: RANKING_SETTINGS_OPTION_FIELDS,
  [WIDGET_TYPE_LOCATION_RANKING]: RANKING_SETTINGS_OPTION_FIELDS,
  [WIDGET_TYPE_RANKING_THRESHOLD]: THRESHOLD_SETTINGS_OPTION_FIELDS,
  [WIDGET_TYPE_REPORT_THRESHOLD]: THRESHOLD_SETTINGS_OPTION_FIELDS,
  [WIDGET_TYPE_CAMERA_GROUP]: CAMERA_GROUP_SETTINGS_OPTION_FIELDS,
  [WIDGET_TYPE_EVENT_TICKER]: TICKER_SETTINGS_OPTION_FIELDS,
};

export const HISTOGRAM_ROLLING_RANGE_SUFFIX = '_rolling';
export const NUMBER_WIDGET_DEFAULT_VIEW = WIDGET_VIEW_SUMMARY;

const NO_SUM_FIELDS = [EVENT_FIELD_STORE_OCCUPANCY_COUNT];

const NO_AVG_FIELDS = [
  EVENT_FIELD_LINE_CROSS_IN,
  EVENT_FIELD_LINE_CROSS_OUT,
  EVENT_FIELD_LINE_CROSS_LEFT,
  EVENT_FIELD_LINE_CROSS_RIGHT,
  EVENT_FIELD_LINE_CROSS_UP,
  EVENT_FIELD_LINE_CROSS_DOWN,
  EVENT_FIELD_LINE_CROSS_TOTAL,
];

class HomeHelper {
  constructor() {
    this.filters = {};
  }

  getStateToCloseConfirmModal = () => ({
    isConfirmModalOpen: false,
  });

  /**
   * Returns a copy of the widget with default values filled in.
   */
  widgetPreProcessForSettings = ({
    widget: sourceWidget,
    noInherit = false,
    isNew = false,
    histogramCustomFieldsDict,
  }) => {
    const {
      id,
      widgetSettings: { type },
    } = sourceWidget;

    const defaultValues = cloneDeep(WIDGET_DEFINITIONS[type]?.initValues);
    if (!isNew) {
      /** Don't merge excludeFromDigest data if it's processing an existing widget.
       * An existing widget could have undefined value for excludeFromDigest, which
       * means it's included in digest. If we merge the default value, {daily: true, weekly: true},
       * it will be displayed as excluded from digest.
       */
      delete defaultValues.widgetSettings.excludeFromDigest;
    }

    // COMBINE DATA SOURCES
    const widget = merge(
      {},
      // Apply defaults
      defaultValues,
      // Then override with any actual saved values
      sourceWidget,
    );

    const { siteIds, widgetSettings } = widget;

    if (noInherit) {
      const { range } = widgetSettings;
      if (range === WIDGET_INHERIT_VALUE) {
        widgetSettings.range = HISTOGRAM_RANGE.DAY;
      }
      if (siteIds.length === 1 && siteIds[0] === WIDGET_INHERIT_VALUE) {
        widget.siteIds = [ALL_LOCATIONS];
      }
    }

    /** Fill in the dataCategory value as it was not saved in the actual widget settings data */
    const dataPaths = widgetSettings.dataPaths || [];
    const dataCategory = widgetSettings.dataCategory;
    if (dataPaths.length > 0 && !dataCategory) {
      widgetSettings.dataCategory =
        histogramCustomFieldsDict[`${dataPaths[0]}`]?.categories?.[0] || WIDGET_DATA_CATEGORY_OTHER;
    }

    CommonWidgetUtils.processWidgetSettings({
      widgetSettings,
      isWidgetSummaryView: this.isWidgetSummaryView(widget),
      ...(CommonWidgetUtils.isPercentageThreshold(widget)
        ? { isThresholdLinePercents: this.isThresholdLinePercentView(id) }
        : {}),
    });

    return widget;
  };

  widgetPreProcessForDisplay = ({
    filters: { date, locationIds: siteIdsFilter, locationTags: locationTagsFilter, range },
    ignoreGlobalFilters = false,
    inheritedValuesOnly = false,
    widget: sourceWidget,
  }) => {
    if (!sourceWidget) {
      return;
    }
    const {
      id,
      siteIds,
      locationTags,
      reportTemplateIds,
      widgetSettings: { type: sourceType },
    } = sourceWidget;
    const sourceRange =
      'range' in sourceWidget.widgetSettings ? sourceWidget.widgetSettings.range : undefined;
    const sourceLinePercents =
      'linePercents' in sourceWidget.widgetSettings
        ? sourceWidget.widgetSettings.linePercents
        : undefined;
    const inheritLocations =
      (siteIds.length === 0 && locationTags.length === 0) ||
      (siteIds[0] === WIDGET_INHERIT_VALUE && locationTags[0] === WIDGET_INHERIT_VALUE);

    const sites = LocationStore.getLocations();
    const type = sourceType || WIDGET_TYPE_REPORT; // Default widget to 'report' type

    const defaultValues = WIDGET_DEFINITIONS[type]?.initValues;

    const globalFilters = ignoreGlobalFilters
      ? {}
      : {
          siteIds: siteIdsFilter,
          locationTags: flatLocationTags(locationTagsFilter),
          widgetSettings: {
            date,
            range,
          },
        };

    let overrideValues = {};
    if (!inheritedValuesOnly) {
      overrideValues = cloneDeep(sourceWidget);
      if (sourceRange === WIDGET_INHERIT_VALUE) {
        delete overrideValues.widgetSettings.range;
      }
      if (inheritLocations) {
        delete overrideValues.siteIds;
        delete overrideValues.locationTags;
      }
      if (!reportTemplateIds?.length) {
        delete overrideValues.reportTemplateIds;
      }
    }

    // COMBINE DATA SOURCES
    const widget = {
      // Apply defaults
      ...defaultValues,
      // Then apply global filters
      ...globalFilters,
      // Then override with any actual saved values
      ...overrideValues,
      widgetSettings: {
        ...defaultValues.widgetSettings,
        ...globalFilters.widgetSettings,
        ...overrideValues?.widgetSettings,
      },
    };

    CommonWidgetUtils.processWidgetSettings({
      type,
      widgetSettings: widget.widgetSettings,
      isWidgetSummaryView: this.isWidgetSummaryView(widget),
      // when user toggle the bar/line view in preview mode without saving, we honour the provided source linePercents value over the saved value in local storage
      ...(CommonWidgetUtils.isPercentageThreshold(widget)
        ? { isThresholdLinePercents: sourceLinePercents ?? this.isThresholdLinePercentView(id) }
        : {}),
    });

    // Verify if all the locations are accessible to the user
    // Ignore the invalid locations
    // If none left after filtering or set to inherit, inherit the current global location filter
    // Use filtered list of locationIds if some of the locations are not verified
    // Fallback to using the locationIds saved in the widget
    if (
      CommonWidgetUtils.isWidgetLimitedByLocations(sourceType) &&
      !isEmpty(siteIds) &&
      !isEmpty(sites)
    ) {
      const verifiedSiteIds = siteIds.filter(
        (id) => find(sites, ['id', id]) || id === WIDGET_INHERIT_VALUE,
      );

      if (isEmpty(verifiedSiteIds) || siteIds.includes(WIDGET_INHERIT_VALUE)) {
        widget.siteIds = ignoreGlobalFilters ? [ALL_LOCATIONS] : siteIdsFilter;
      } else if (verifiedSiteIds.length !== siteIds.length) {
        widget.siteIds = verifiedSiteIds;
      } else {
        widget.siteIds = siteIds;
      }
    }

    return widget;
  };

  widgetPreProcessForThreshold = ({ widget, rangeFilter, allLocationsCount = 0 }) => {
    const { siteIds, locationTags, reportTemplateIds, widgetSettings, thresholds } = widget;
    const { range, rangeRolling, rankBy } = widgetSettings;
    const isCreatingNew = !thresholds?.length;
    const isReport = CommonWidgetUtils.isReport(widget);
    const isRanking = CommonWidgetUtils.isRanking(widget);

    const getInitialRange = (range, rangeFilter) => {
      let initialRange = range;
      if (range === WIDGET_INHERIT_VALUE) {
        initialRange = rangeFilter;
      }
      if (initialRange === HISTOGRAM_RANGE.YEAR) {
        // we don't support year threshold yet, so force it to be month instead
        initialRange = HISTOGRAM_RANGE.MONTH;
      }
      return initialRange;
    };

    if (isCreatingNew) {
      const isRankingByLocation = CommonWidgetUtils.isWidgetRankingByLocations(rankBy);
      /**
       * If ranking widget is ranked by locations, take it as long ranking if locations count met the condition.
       * If ranking widget is not ranked by locations, take it as long ranking by default.
       */
      const isLongRanking =
        isRanking &&
        ((isRankingByLocation && allLocationsCount > LONG_RANKING_SIZE) || !isRankingByLocation);
      return {
        siteIds,
        locationTags,
        reportTemplateIds,
        ownerType: 'user',
        description: '',
        thresholds: INITIAL_THRESHOLDS,
        widgetSettings: {
          ...widgetSettings,
          range: getInitialRange(range, rangeFilter),
          /** threshold only support rolling weeks not other rolling ranges */
          rangeRolling: range === HISTOGRAM_RANGE.WEEK ? rangeRolling : false,
          dataCategory: 'Other',
          width: 1,
          height: isLongRanking ? 2 : 1,
          summaryView: !isReport,
          tags: [WIDGET_TAG_THRESHOLDS],
          type: isReport ? WIDGET_TYPE_REPORT_THRESHOLD : WIDGET_TYPE_RANKING_THRESHOLD,
          excludeFromDigest: { daily: true, weekly: true },
          ...(isRanking && { size: THRESHOLD_RANKING_MAX_ITEMS }),
          ...(isReport && { showPreviousBars: false, compareRange: false }),
        },
      };
    } else {
      /** remove the VD added fetch data that's for the chart display */
      const widgetWithoutFetchData = omit(widget, [
        'dataSets',
        'dataSetTypes',
        'error',
        'fetched',
        'fetching',
        'isEmpty',
        'range',
        'lastFetchDataParams',
      ]);
      return {
        ...widgetWithoutFetchData,
        widgetSettings: {
          ...widgetSettings,
          dataCategory: 'Other',
        },
      };
    }
  };

  thresholdPreProcessForDisplay = (sourceWidget) => {
    const widget = clone(sourceWidget);
    const { range, type } = sourceWidget.widgetSettings;
    const isReport = CommonWidgetUtils.isReportWidget(type);
    return merge({}, widget, {
      widgetSettings: { range: isReport ? THRESHOLD_RANGE_MAP[range] : range },
    });
  };

  isWidgetSummaryView = (widget) => {
    const { id, widgetSettings } = widget;
    const { type, range } = widgetSettings;
    const localStorageViewSettings =
      StorageAPI.getLocally(WIDGETS_VIEW_SETTINGS) ||
      StorageAPI.getLocally(RANKING_WIDGETS_VIEW_SETTINGS) || // legacy from when only ranking widgets can toggle different view
      {};
    const isReport = CommonWidgetUtils.isReportWidget(type);
    const isRanking = CommonWidgetUtils.isRankingWidget(type);
    const isNumber = CommonWidgetUtils.isSimpleNumberWidget(widgetSettings);
    const isThreshold = CommonWidgetUtils.isThreshold(widget);
    const isHour = range === HISTOGRAM_RANGE.HOUR;
    let defaultViewName = REPORT_WIDGET_DEFAULT_VIEW;
    if (isRanking) {
      defaultViewName = RANKING_WIDGET_DEFAULT_VIEW;
    } else if (isNumber) {
      defaultViewName = NUMBER_WIDGET_DEFAULT_VIEW;
    } else if (isReport && isHour && !isThreshold) {
      defaultViewName = HOUR_REPORT_WIDGET_DEFAULT_VIEW;
    }
    return get(localStorageViewSettings, [id], defaultViewName) === WIDGET_VIEW_SUMMARY;
  };

  isThresholdLinePercentView = (id) => {
    const localStorageViewSettings = StorageAPI.getLocally(PERCENT_THRESHOLD_VIEW_SETTINGS) || {};
    return (
      get(localStorageViewSettings, [id], DEFAULT_PERCENT_THRESHOLD_VIEW) ===
      PERCENT_THRESHOLD_VIEW_LINE
    );
  };

  // update a widget's view setting in local storage
  updateLocalWidgetViewSetting = ({ id, summaryView }) => {
    const localStorageViewSettings =
      StorageAPI.getLocally(WIDGETS_VIEW_SETTINGS) ||
      StorageAPI.getLocally(RANKING_WIDGETS_VIEW_SETTINGS) || // legacy from when only ranking widgets can toggle different view
      {};
    localStorageViewSettings[id] = this.getWidgetViewName(summaryView);
    StorageAPI.storeLocally(WIDGETS_VIEW_SETTINGS, localStorageViewSettings);
  };

  // update a percentage threshold widget's view setting in local storage
  updateLocalPercentThresholdViewSettings = ({ id, linePercents }) => {
    const localStorageViewSettings = StorageAPI.getLocally(PERCENT_THRESHOLD_VIEW_SETTINGS) || {};
    localStorageViewSettings[id] = linePercents
      ? PERCENT_THRESHOLD_VIEW_LINE
      : PERCENT_THRESHOLD_VIEW_BAR;
    StorageAPI.storeLocally(PERCENT_THRESHOLD_VIEW_SETTINGS, localStorageViewSettings);
  };

  getWidgetViewName = (summaryView) => (summaryView ? WIDGET_VIEW_SUMMARY : WIDGET_VIEW_DETAIL);

  getPercentThresholdViewName = (linePercents) =>
    linePercents ? PERCENT_THRESHOLD_VIEW_LINE : PERCENT_THRESHOLD_VIEW_BAR;

  stripInheritableValues = (widget) => {
    each(widget, (sourceValue, prop) => {
      if (isPlainObject(sourceValue)) {
        widget[prop] = this.stripInheritableValues(sourceValue);
      } else {
        const value = isArray(sourceValue) ? sourceValue[0] : sourceValue;
        const isValueInherited = value === WIDGET_INHERIT_VALUE;
        const isArrayInherited = isArray(value) && value[0] === WIDGET_INHERIT_VALUE;
        if (isValueInherited || isArrayInherited) {
          delete widget[prop];
        }
      }
    });
    return widget;
  };

  // Process the widget data for saving to the backend
  processWidgetForSaving = ({ eventStandard, sourceWidget, locale }) => {
    const {
      widgetSettings: { type },
    } = sourceWidget;

    const defaultValues = WIDGET_DEFINITIONS[type]?.initValues;

    // Remove props that are set to inherit
    const sanitizedSource = cloneDeep(pickBy(sourceWidget, identity));

    // COMBINE DATA SOURCES
    const mergedWidget = merge(
      {},
      // Apply defaults
      defaultValues,
      // Then override with any actual saved values
      sanitizedSource,
    );

    const widget = this.stripInheritableValues(mergedWidget);

    // Remove the _ALL_EVENTS values
    widget.reportTemplateIds = this.removeAllEventsReportId(widget.reportTemplateIds);

    // Apply sanitization logic
    const sanitizedWidget = sanitizeWidget(widget);
    widget.widgetSettings = omit(sanitizedWidget.widgetSettings, [
      'date',
      'dataCategory',
      'dataPathRatioWith',
      'ratioWithFieldName',
    ]);

    // Set default description for ranking widgets if no description, not for threshold widgets
    if (CommonWidgetUtils.isRankingWidget(type) && !CommonWidgetUtils.isThreshold(widget)) {
      const {
        description,
        widgetSettings: { dataPaths, rankBy, size, dataStatistic },
      } = widget;

      if (!description) {
        let rankTypeName = '';
        if (CommonWidgetUtils.isWidgetRankingByLocations(rankBy)) {
          rankTypeName = locale.general.locations;
        } else {
          rankTypeName = this.getStandardFieldName({
            fallbackName: rankBy,
            fieldId: rankBy,
            localeCode: locale.locale,
            name: RANK_BY_NAME,
            eventStandard,
          });
        }

        const dataFieldName = getRankingWidgetDataFieldName({
          eventStandard,
          fallbackName: dataPaths?.[0],
          dataPaths,
          localeCode: locale.locale,
          localizationNameType: dataStatistic,
        });

        const defaultDescription = LocaleUtil.getLocalizedStr({
          string: locale.dashboard.defaultRankingWidgetDescription,
          replacements: {
            rankSize: size,
            rankTypeName: rankTypeName.toLowerCase(),
            dataFieldName: dataFieldName.toLowerCase(),
          },
        });
        set(widget, 'description', defaultDescription);
      }
    }
    const siteIds = widget.siteIds || [];
    const deviceIds = determineDeviceIds(siteIds);

    return {
      description: widget.description,
      locationIds: deviceIds,
      siteIds,
      locationTags: widget.locationTags || [],
      name: widget.name,
      reportTemplateIds: widget.reportTemplateIds,
      widgetSettings: widget.widgetSettings,
      thresholds: widget.thresholds || [],
    };
  };

  set initialFilters(filters) {
    this.filters = filters;
  }

  get initialFilters() {
    return this.filters;
  }

  canFilterReset = ({ filter, filters }) => {
    let defaultValue = this.filters[filter];
    let currentValue = get(filters, filter);
    defaultValue = get(defaultValue, 'id', defaultValue);
    currentValue = get(currentValue, 'id', currentValue);
    return defaultValue && !isEqual(currentValue, defaultValue);
  };

  canFiltersReset = (filters) => {
    let canReset = false;
    for (const filter in filters) {
      if (this.canFilterReset({ filter, filters })) {
        canReset = true;
        break;
      }
    }
    return canReset;
  };

  resetFilters = (dispatch) => {
    return () => {
      if (this.filters) {
        dispatch(setFilter(this.filters));
      }
    };
  };

  isWidgetDefault = ({ ownerType }) => ownerType !== 'user';

  isWidgetDefaultOrCustomized = ({ ownerType, rootWidgetId }) =>
    ownerType !== 'user' || (ownerType === 'user' && rootWidgetId !== null);

  isWidgetCustomized = ({ ownerType, rootWidgetId }) =>
    ownerType === 'user' && rootWidgetId !== null;

  isWidgetUserOnly = ({ ownerType, rootWidgetId }) => ownerType === 'user' && rootWidgetId === null;

  // Show delete if its a user widget...
  // or user is type `admin` and the widget `ownerType` is `customer` and not inherited
  // or impersonator user `userType` is an equal or higher level than the widget `ownerType`
  // Note: this delete may still fail if the customer widget has been customized
  // TODO Improvement: hide it in OR case if this widget has child widgets
  // GET /api/widgets/count?filter={"parentWidgetId":"CUSTOMER_WIDGET_ID"}
  isDeleteActionVisible = ({ ownerType, rootWidgetId }) => {
    const isUserWidgetOnly = this.isWidgetUserOnly({ ownerType, rootWidgetId });
    const impersonatorUserType = get(AuthService.impersonator, 'profile.userType');
    const adminCanDelete =
      AuthService.session.userType === USER_TYPE_ADMIN &&
      ownerType === 'customer' &&
      rootWidgetId === null;
    const impersonatorCanDelete = HierarchyUtil.isTypeEqualOrHigher({
      type: impersonatorUserType,
      minType: ownerType,
    });
    const userCanDelete = adminCanDelete || impersonatorCanDelete;
    return isUserWidgetOnly || userCanDelete;
  };

  removeAllEventsReportId = (reportTemplateIds) =>
    filter(reportTemplateIds, (id) => id !== ALL_EVENTS_REPORT);

  getLocation = ({ locationIds, locale, locations }) => {
    if (locationIds.length > 1) {
      return `${locationIds.length} ${locale.general.locations}`;
    } else if (isEmpty(locations)) {
      return '';
    } else {
      const location = locations.find((item) => item.id === locationIds[0]);
      return get(location, 'name', '');
    }
  };

  getReportOptions = ({ locale, reports, locations, isReportOwnerTypeValid = () => true }) => {
    if (isEmpty(reports)) {
      return [];
    }

    const { localeCode } = locale;
    const reportOptions = [];
    reports.forEach((report) => {
      const { availableFeatures } = getAvailableFeatures(featureStandard, locations);
      const reportFeatures =
        report.metadata?.categories?.filter((category) => category !== constants.ALL_FEATURE) || [];
      const hasFeatureAccess =
        reportFeatures.length === 0 ||
        intersection(Object.keys(availableFeatures), reportFeatures)?.length > 0;

      // TODO: Why report.reports is an array? Can it has multiple reports inside?
      const specificReports = report.reports;
      const isOwnerTypeValid = specificReports.some(
        (specificReport) =>
          isReportOwnerTypeValid(specificReport) &&
          (specificReport.inheritType === 'all' || specificReport.ownerType === 'user'),
      );

      // Widgets can only be made from Reports that are v2+
      const version = report.metadata?.version ?? 0;

      if (version >= 2 && hasFeatureAccess && isOwnerTypeValid) {
        const option = {
          value: report.templateId,
          label: LocaleUtil.getValueByLocale({ values: report.name, localeCode }),
        };
        reportOptions.push(option);
      }
    });
    reportOptions.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'label'));
    reportOptions.unshift({
      value: ALL_EVENTS_REPORT,
      label: locale.reports.allEvents,
      isUnique: true,
    });
    return reportOptions;
  };

  getReportById = ({ reports, templateId }) => {
    if (isEmpty(reports)) {
      return;
    }
    return reports.find((item) => item.templateId === templateId);
  };

  getCameraGroups = ({ cameraGroups, locale }) => {
    if (isEmpty(cameraGroups)) {
      return [];
    }
    const groupOptions = [];
    cameraGroups.forEach(({ name, id }) => {
      const isRecent = name === constants.RECENT_CAMERAS_GROUP_NAME;
      const isFavourite = name === constants.FAVOURITE_CAMERAS_GROUP_NAME;
      if (!isRecent && !isFavourite) {
        groupOptions.push({
          value: id,
          label: name,
        });
      }
    });
    groupOptions.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'label'));
    groupOptions.unshift({
      value: constants.CAMERA_GROUP_FAVORITES,
      label: locale.camera.favourites,
    });
    groupOptions.unshift({
      value: constants.CAMERA_GROUP_RECENT,
      label: locale.camera.recent,
    });
    return groupOptions;
  };

  getInheritableSelectOption = ({ labelText, locale }) => {
    return {
      value: WIDGET_INHERIT_VALUE,
      label: isString(labelText)
        ? `${locale.dashboard.updateWithSelectedDashboardFilter} (${labelText})`
        : locale.dashboard.updateWithSelectedDashboardFilter,
      isUnique: true,
    };
  };

  getOptionsFromValues = ({ locale, options, prop, values }) => {
    // The prop `rangeRollingAlternates` is alternate options for `range`
    const remappedProp = prop === 'rangeRollingAlternates' ? 'range' : prop;
    const propValue = CommonUtil.findKeyValueInObject({ object: values, key: remappedProp });
    const propObj = isArray(propValue)
      ? find(options, ['value', propValue[0]])
      : find(options, ['value', propValue]);
    const labelText = get(propObj, 'inheritLabel', get(propObj, 'label'));
    const inheritable = this.getInheritableSelectOption({ labelText, locale });
    return [inheritable, ...options];
  };

  termToOption = (term) => ({
    value: term,
    label: term,
  });

  getHighlightWords = ({ highlightWords }) => {
    const wordsFromCH = get(AuthService.session, 'clientSettings.eventTicker.highlightWords', []);
    const inheritedArray = wordsFromCH.map(this.termToOption);
    const wordsArray = highlightWords ? highlightWords.map(this.termToOption) : [];
    return merge(inheritedArray, wordsArray);
  };

  getOrderOptions = ({ locale }) => {
    return [
      {
        value: SORT_DESC,
        label: locale.topHistogram.descending,
      },
      {
        value: SORT_ASC,
        label: locale.topHistogram.ascending,
      },
    ];
  };

  isReportDataLegacyCount = (dataPath) =>
    CommonWidgetUtils.isLegacyDataPath(dataPath) &&
    (dataPath.includes('count') || dataPath === WIDGET_INHERIT_VALUE);

  isRankingDataLegacyCount = (dataPath) =>
    RANKING_WIDGET_LEGACY_DATA_PATHS.includes(dataPath) &&
    (dataPath.includes('count') || dataPath === RANKING_WIDGET_DEFAULT_FIELD_NAME);

  isLegacyTotalAmount = (dataPath) => dataPath === WIDGET_LEGACY_DATA_PATH_TOTAL_AMOUNT;

  getRankByOptions = ({ eventStandard, rankByFieldsDict, locale }) => {
    const options = [
      {
        value: RANKING_WIDGET_DEFAULT_RANK_BY_LOCATION,
        label: locale.reports.summaryLocation,
      },
    ];
    forIn(rankByFieldsDict, (field, fieldName) => {
      options.push({
        value: fieldName,
        label: this.getStandardFieldName({
          fieldId: fieldName,
          fallbackName: fieldName,
          localeCode: locale.locale,
          eventStandard,
        }),
      });
    });
    return options;
  };

  getStandardFieldName = ({
    fallbackName = '',
    fieldId,
    localeCode,
    name = 'fullName',
    eventStandard,
  }) => {
    return CommonWidgetUtils.getStandardFieldName({
      eventStandard,
      fallbackName,
      fieldName: fieldId,
      localeCode,
      localizationNameType: name,
    });
  };

  getRangeOptions = ({ locale, isSavedHour = false, isThreshold = false }) => {
    const canCreateRealTimeWidgets = FeaturesUtil.canCreateRealTimeWidgets();
    const hasHour = canCreateRealTimeWidgets || isSavedHour;
    const hasYear = !isThreshold;
    const options = [
      hasHour && {
        value: HISTOGRAM_RANGE.HOUR,
        label: locale.date.hour, //'Hour',
      },
      {
        value: HISTOGRAM_RANGE.DAY,
        label: locale.date.day, //'Day',
      },
      {
        value: HISTOGRAM_RANGE.WEEK,
        label: locale.date.week, //'Week',
      },
      {
        value: HISTOGRAM_RANGE.MONTH,
        label: locale.date.month, //'Month',
      },
      hasYear && {
        value: HISTOGRAM_RANGE.YEAR,
        label: locale.date.year, //'Year',
      },
    ];
    return options.filter(Boolean);
  };

  getRollingRangeOptions = ({ locale, isThreshold = false }) => {
    const options = [
      !isThreshold && {
        value: HISTOGRAM_RANGE.HOUR,
        label: locale.histogram.lastSixtyMinutes, //'Last 60 Minutes',
      },
      !isThreshold && {
        value: HISTOGRAM_RANGE.DAY,
        label: locale.histogram.lastTwentyFourHours, //'Last 24 Hours',
      },
      {
        value: HISTOGRAM_RANGE.WEEK,
        label: locale.histogram.lastSevenDays, //'Last 7 Days,
      },
      !isThreshold && {
        value: HISTOGRAM_RANGE.MONTH,
        label: locale.histogram.lastFourWeeks, //'Last 4 Weeks',
      },
      !isThreshold && {
        value: HISTOGRAM_RANGE.YEAR,
        label: locale.histogram.lastTwelveMonths, //'Last 12 Months',
      },
    ];
    return options.filter(Boolean);
  };

  /**
   * Returns range options with rolling range alternates, so all options are available in one dropdown
   */
  getMixedRangeOptions = ({ locale, isSavedHour = false, isThreshold = false }) => {
    const rollingRangeOptions = this.getRollingRangeOptions({
      locale,
      isThreshold,
    }).map((option) => ({
      ...option,
      value: `${option.value}${HISTOGRAM_ROLLING_RANGE_SUFFIX}`, // add a suffix to differentiate from regular range options
    }));
    return [...this.getRangeOptions({ locale, isSavedHour, isThreshold }), ...rollingRangeOptions];
  };

  getLegacyDataPathOptions = ({ locale, isTickerWidget, isRankingWidget }) => {
    return [
      ...(isTickerWidget
        ? []
        : [
            {
              value: WIDGET_DATA_PATH_BOOKMARKS_COUNT,
              label: locale.dashboard.bookmarksCount,
              inheritLabel: locale.dashboard.inheritBookmarksCount,
            },
            {
              value: WIDGET_DATA_PATH_MOTIONS_COUNT,
              label: locale.dashboard.motionsCount,
              inheritLabel: locale.dashboard.inheritMotionsCount,
            },
            {
              value: isRankingWidget
                ? FIELD_NAME_EMPTY
                : WIDGET_LEGACY_DATA_PATH_TRANSACTIONS_COUNT,
              label: locale.dashboard.transactionsCount,
              inheritLabel: locale.dashboard.inheritTransactionsCount,
            },
            {
              value: WIDGET_DATA_PATH_EVENTS_COUNT,
              label: locale.dashboard.eventsCount,
              inheritLabel: locale.dashboard.inheritEventsCount,
            },
          ]),
      {
        value: WIDGET_LEGACY_DATA_PATH_TOTAL_AMOUNT,
        label: locale.dashboard.transactionsTotalAmount,
        inheritLabel: locale.dashboard.inheritTransactionsTotalAmount,
      },
    ];
  };

  getDataPathOptions = ({
    dataCategory,
    histogramCustomFieldsDict,
    locale,
    eventStandard,
    type,
  }) => {
    let dataPathOptions = [];

    const localeCode = locale.locale;
    const isTickerWidget = type === WIDGET_TYPE_EVENT_TICKER;
    const isRankingWidget = CommonWidgetUtils.isRankingWidget(type);
    const isDataCategorySpecific = dataCategory !== WIDGET_DATA_CATEGORY_OTHER;

    const histogramFieldsInCategory = [];
    const dataPathsNotBelongToAnyCategory = [];

    forIn(histogramCustomFieldsDict, (field, fieldName) => {
      const categories = get(field, 'categories', []);
      const fieldLabel = this.getStandardFieldName({
        fieldId: fieldName,
        fallbackName: fieldName,
        localeCode,
        name: 'sum',
        eventStandard,
      });
      const fieldOption = {
        value: fieldName,
        label: fieldLabel,
        inheritLabel: fieldName,
      };

      if (categories.includes(dataCategory)) {
        histogramFieldsInCategory.push(fieldOption);
      } else if (isEmpty(categories)) {
        dataPathsNotBelongToAnyCategory.push(fieldOption);
      }
    });

    const legacyDataPaths = this.getLegacyDataPathOptions({
      locale,
      isTickerWidget,
      isRankingWidget,
    });

    dataPathOptions = isDataCategorySpecific
      ? histogramFieldsInCategory
      : [...legacyDataPaths, ...dataPathsNotBelongToAnyCategory];

    dataPathOptions.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'label'));

    return dataPathOptions;
  };

  canCalConversionRate = ({ dataPath, histogramCustomFieldsDict }) => {
    const isLineCrossInFieldAvailable =
      !!histogramCustomFieldsDict[constants.EVENT_FIELD_LINE_CROSS_IN];
    return (
      isLineCrossInFieldAvailable &&
      (dataPath === WIDGET_LEGACY_DATA_PATH_TRANSACTIONS_COUNT ||
        dataPath === RANKING_WIDGET_DEFAULT_FIELD_NAME)
    );
  };

  canDataPathCalPercentage = ({
    dataPathRatioWithFields,
    eventStandard,
    dataPath,
    histogramCustomFieldsDict,
  }) => {
    const dataPathPercentageFields = pickBy(dataPathRatioWithFields, (field, fieldName) =>
      canCalPercentage(eventStandard, [dataPath, fieldName]),
    );
    return (
      !isEmpty(dataPathPercentageFields) ||
      this.canCalConversionRate({
        dataPath,
        histogramCustomFieldsDict,
      })
    );
  };

  getDataStatisticOptions = ({
    eventStandard,
    locale,
    histogramCustomFieldsDict,
    dataPath,
    isTickerWidget,
  }) => {
    const dataPathStandardField = histogramCustomFieldsDict[dataPath];
    const dataPathRatioWithFields = dataPathStandardField?.features?.ratioWith;

    const unitType = dataPathStandardField?.unitType ?? WIDGET_DATA_UNIT_TYPE_QUANTITY;

    const canDataPathCalPercentage = this.canDataPathCalPercentage({
      eventStandard,
      dataPath,
      histogramCustomFieldsDict,
      dataPathRatioWithFields,
    });
    const canDataPathCalRatio = !isEmpty(dataPathRatioWithFields);

    const isUnitTypeCurrency = CommonWidgetUtils.isUnitTypeCurrency(unitType);
    const isUnitTypeQuantity = CommonWidgetUtils.isUnitTypeQuantity(unitType);
    const isUnitTypeRatio = CommonWidgetUtils.isUnitTypeRatio(unitType);
    const isReportDataLegacyCount = this.isReportDataLegacyCount(dataPath);
    const isRankingDataLegacyCount = this.isRankingDataLegacyCount(dataPath);

    const isNoSumField = NO_SUM_FIELDS.includes(dataPath);
    const isNoAvgField = NO_AVG_FIELDS.includes(dataPath);

    let statisticPercentageLabel = locale.dashboard.percentage;
    if (
      canDataPathCalPercentage &&
      dataPathRatioWithFields &&
      Object.keys(dataPathRatioWithFields).length === 1
    ) {
      if (isUnitTypeCurrency) {
        statisticPercentageLabel = locale.dashboard.percentageOfNetSales;
      } else if (isUnitTypeQuantity) {
        statisticPercentageLabel = locale.dashboard.percentageOfItems;
      }
    }

    const options = [
      !isUnitTypeRatio &&
        !isRankingDataLegacyCount &&
        !isReportDataLegacyCount &&
        !isNoSumField && {
          value: WIDGET_DATA_STATISTIC_SUM,
          label: locale.dashboard.sum,
        },
      !isReportDataLegacyCount &&
        !isRankingDataLegacyCount &&
        !isNoAvgField && {
          value: WIDGET_DATA_STATISTIC_AVG,
          label: locale.dashboard.average,
        },
      (isReportDataLegacyCount || isRankingDataLegacyCount) && {
        value: WIDGET_DATA_STATISTIC_COUNT,
        label: locale.histogram.count,
      },
      canDataPathCalPercentage &&
        !isTickerWidget &&
        !isUnitTypeRatio && {
          value: WIDGET_DATA_STATISTIC_PERCENTAGE,
          label: statisticPercentageLabel,
        },
      canDataPathCalRatio &&
        !isTickerWidget &&
        !isUnitTypeRatio && {
          value: WIDGET_DATA_STATISTIC_RATIO,
          label: locale.dashboard.ratio,
        },
    ];
    return options.filter(Boolean);
  };

  getGraphTypeOptions = ({
    locale,
    compareVsAllCustomerLocations,
    showPreviousBars,
    hasMultipleReports,
  }) => {
    let options = [
      {
        value: GRAPH_TYPE.BAR,
        label: locale.dashboard.barGraph,
      },
    ];
    if (!compareVsAllCustomerLocations) {
      options = options.concat([
        {
          value: GRAPH_TYPE.HORZ_BAR,
          label: locale.dashboard.horzBarGraph,
        },
        {
          value: GRAPH_TYPE.AREA,
          label: locale.dashboard.areaGraph,
        },
        {
          value: GRAPH_TYPE.LINE,
          label: locale.dashboard.lineGraph,
        },
        {
          value: GRAPH_TYPE.STACKED_BAR,
          label: locale.dashboard.stackedBarGraph,
        },
        {
          value: GRAPH_TYPE.STACKED_HORZ_BAR,
          label: locale.dashboard.stackedHorzBarGraph,
        },
        {
          value: GRAPH_TYPE.STACKED_AREA,
          label: locale.dashboard.stackedAreaGraph,
        },
      ]);

      if (!showPreviousBars && !hasMultipleReports) {
        options.push({
          value: GRAPH_TYPE.NUMBER,
          label: locale.dashboard.number,
        });
      }
    }
    return options.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'label'));
  };

  getSelectOptions = ({
    eventStandard,
    featureStandard,
    cameraGroups,
    filters,
    histogramCustomFieldsDict,
    locale,
    locations,
    rankByFieldsDict,
    reports,
    values,
    isThreshold = false,
    devicesBySiteId,
  }) => {
    const localeCode = locale.locale;
    const { ownerType, widgetSettings, reportTemplateIds } = values;
    const dataCategory = widgetSettings?.dataCategory;
    const dataPaths = widgetSettings?.dataPaths;
    const dataStatistic = widgetSettings?.dataStatistic;
    const fieldName = widgetSettings?.fieldName;
    const type = widgetSettings?.type;
    const isTickerWidget = type === WIDGET_TYPE_EVENT_TICKER;
    const compareVsAllCustomerLocations = widgetSettings?.compareVsAllCustomerLocations;
    const showPreviousBars = widgetSettings?.showPreviousBars;
    const hasMultipleReports = reportTemplateIds?.length > 1;
    const inheritedValues = widgetSettings
      ? this.widgetPreProcessForDisplay({
          filters,
          inheritedValuesOnly: true,
          widget: values,
        })
      : values;
    const { fields, nonInheritableFields } = WIDGET_SETTINGS_OPTION_FIELDS[type];

    const isReportOwnerTypeValid = (report) =>
      HierarchyUtil.isTypeEqualOrHigher({
        type: report.ownerType,
        minType: ownerType,
      });

    this.dataPathRatioWithOptions = [];
    const isRankingWidget = type === WIDGET_TYPE_LOCATION_RANKING || type === WIDGET_TYPE_RANKING;

    let dataPath;
    if (isRankingWidget) {
      dataPath = fieldName;
    } else {
      dataPath = isArray(dataPaths) ? dataPaths[0] : dataPaths;
    }

    const dataPathStandardField = get(histogramCustomFieldsDict, dataPath);
    const dataPathRatioWithFields = get(dataPathStandardField, 'features.ratioWith');

    const canDataPathCalRatio = !isEmpty(dataPathRatioWithFields);
    const canDataPathCalPercentage = this.canDataPathCalPercentage({
      eventStandard,
      dataPath,
      histogramCustomFieldsDict,
      dataPathRatioWithFields,
    });

    if (canDataPathCalRatio) {
      for (const ratioWithField in dataPathRatioWithFields) {
        if (
          histogramCustomFieldsDict[ratioWithField] &&
          (CommonWidgetUtils.isShowingRatio(dataStatistic) ||
            (CommonWidgetUtils.isShowingPercentage(dataStatistic) &&
              canCalPercentage(eventStandard, [dataPath, ratioWithField])))
        ) {
          this.dataPathRatioWithOptions.push({
            value: ratioWithField,
            label:
              get(dataPathRatioWithFields, `['${ratioWithField}'].${localeCode}.sum`) ||
              get(dataPathRatioWithFields, `['${ratioWithField}'].${localeCode}.fullName`) ||
              get(dataPathRatioWithFields, `['${ratioWithField}'].en.fullName`),
          });
        }
      }
    }

    // Usually, the options for ratio and percentage has no difference.
    // But for the conversion rate, the hardcoded option is only available for percentage.
    if (canDataPathCalPercentage) {
      const canCalConversionRate = this.canCalConversionRate({
        dataPath,
        histogramCustomFieldsDict,
      });
      if (canCalConversionRate) {
        this.dataPathRatioWithOptions.push({
          value: constants.EVENT_FIELD_LINE_CROSS_IN,
          label:
            histogramCustomFieldsDict[constants.EVENT_FIELD_LINE_CROSS_IN]?.i18n?.[localeCode]
              ?.fullName,
        });
      }
    }

    const definedCategories = [];
    forIn(histogramCustomFieldsDict, (field) => {
      const categories = get(field, 'categories', []);

      for (const category of categories) {
        if (!find(definedCategories, ['value', category])) {
          definedCategories.push({
            value: category,
            label: get(field, `i18n.${localeCode}.categoryNames.${category}`, category),
          });
        }
      }
    });
    definedCategories.sort((a, b) => CommonUtil.sortObjectAlphabetically(a, b, 'label'));
    this.dataCategoryOptions = [
      ...definedCategories,
      {
        value: WIDGET_DATA_CATEGORY_OTHER,
        label: locale.general.other,
      },
    ];

    const dataPathOptions = this.getDataPathOptions({
      eventStandard,
      histogramCustomFieldsDict,
      dataCategory,
      locale,
      type,
    });

    const isSavedHour = widgetSettings?.range === HISTOGRAM_RANGE.HOUR;

    const sourceOptions = {
      range: isThreshold
        ? this.getMixedRangeOptions({ locale, isSavedHour, isThreshold })
        : this.getRangeOptions({ locale, isSavedHour }),
      rangeRollingAlternates: this.getRollingRangeOptions({ locale, isThreshold }),
      cameraGroupId: this.getCameraGroups({ cameraGroups, locale }),
    };

    const highlightWords = get(values, 'widgetSettings.highlightWords[0].terms', []);

    let tagsOptions = getWidgetTagsOptions({
      featureStandard,
      localeCode,
      tags: AuthService.getSession()?.widgetMetadata?.tags ?? [],
    });
    const thresholdOption = tagsOptions.find(({ value }) => value === WIDGET_TAG_THRESHOLDS);
    if (thresholdOption) {
      if (isThreshold) {
        // the 'thresholds' tag is enforced for threshold widgets
        thresholdOption.isFixed = true;
      } else {
        tagsOptions = tagsOptions.filter(({ value }) => value !== WIDGET_TAG_THRESHOLDS);
      }
    }

    const nonInheritableOptions = {
      graphType: this.getGraphTypeOptions({
        locale,
        compareVsAllCustomerLocations,
        showPreviousBars,
        hasMultipleReports,
      }),
      dataCategory: this.dataCategoryOptions,
      dataPaths: dataPathOptions,
      dataPathRatioWith: this.dataPathRatioWithOptions,
      dataStatistic: this.getDataStatisticOptions({
        eventStandard,
        locale,
        histogramCustomFieldsDict,
        dataPath,
        isTickerWidget,
      }),
      fieldName: dataPathOptions,
      highlightWords: this.getHighlightWords({ highlightWords }),
      order: this.getOrderOptions({ locale }),
      rangeDetails: getRangeDetailOptions({ localeCode, isThreshold }),
      rankBy: this.getRankByOptions({ eventStandard, locale, rankByFieldsDict }),
      reportTemplateIds: this.getReportOptions({
        locale,
        reports,
        isReportOwnerTypeValid,
        locations,
        devicesBySiteId,
      }),
      tags: tagsOptions,
    };

    // Generate options with inheritable option included
    const options = {};
    each(sourceOptions, (value, prop) => {
      if (fields.includes(prop)) {
        if (nonInheritableFields.includes(prop)) {
          options[prop] = value;
        } else {
          options[prop] = this.getOptionsFromValues({
            locale,
            options: value,
            prop,
            values: inheritedValues,
          });
        }
      }
    });

    // Add in any options that don't need an inheritable option
    return {
      ...options,
      ...nonInheritableOptions,
    };
  };

  getWidgetFilters = ({ filters, widget }) => {
    const { date, locationIds: siteIdsFilter, range } = filters;
    const {
      widgetSettings: { type },
    } = widget;

    const defaultValues = WIDGET_DEFINITIONS[type]?.initValues;

    const globalFilters = {
      siteIds: siteIdsFilter,
      widgetSettings: {
        date,
        range,
      },
    };

    // COMBINE DATA SOURCES
    const combined = merge(
      {},
      // Apply defaults
      defaultValues,
      // Then apply global filters
      globalFilters,
      // Then override with any actual saved values
      widget,
    );

    // Report only filter data
    return {
      date: combined.widgetSettings.date,
      location: combined.siteIds,
      range: combined.widgetSettings.range,
      rangeDetails: combined.widgetSettings.rangeDetails,
    };
  };

  getFormattedPathSubType = (subType) => {
    switch (subType) {
      case 'count':
        return 'Count';

      case 'totalAmountSum':
        return 'Total Amount';

      default:
        return subType;
    }
  };

  getDataFromWidget = ({ histogramCustomFieldsDict, locale, locations, reports, widget }) => {
    const {
      widgetSettings: { type },
    } = widget;

    switch (type) {
      case WIDGET_TYPE_RANKING:
      case WIDGET_TYPE_LOCATION_RANKING:
      case WIDGET_TYPE_RANKING_THRESHOLD:
        return this.getRankingWidgetData({ histogramCustomFieldsDict, locale, locations, widget });
      case WIDGET_TYPE_REPORT:
      case WIDGET_TYPE_REPORT_THRESHOLD:
      default:
        return this.getReportWidgetData({ locale, reports, widget });
    }
  };

  getRankingWidgetData = ({ histogramCustomFieldsDict, locale, locations, widget }) => {
    const {
      dataSets,
      widgetSettings: { dataStatistic, fieldName, rankBy },
    } = widget;

    if (!dataSets) {
      return [];
    }

    // TODO: these doesn't seem to work....
    const data = [];
    let type;
    let path;
    if (fieldName === FIELD_NAME_EMPTY) {
      type = locale.fieldName.fieldNameEmpty; // "Transaction Count"
      path = 'count';
    } else {
      type = get(
        histogramCustomFieldsDict,
        `['${fieldName}'].i18n.${locale.locale}.${dataStatistic}`,
      );
      path = dataStatistic;
    }

    dataSets.forEach((dataSet) => {
      let title;
      const uniqueId = get(dataSet, 'key');

      if (CommonWidgetUtils.isWidgetRankingByLocations(rankBy)) {
        const location = locations.find(({ id }) => id === uniqueId);
        title = get(location, 'name', '');
      } else {
        title = uniqueId;
      }

      if (title) {
        dataSet.results.forEach((datum) => {
          const time = get(datum, 'startTime');
          const value = get(datum, `stats.${path}`, 0);
          data.push({
            'data-set': title,
            type,
            value,
            'start-time': moment(time).toISOString(),
          });
        });
      } else {
        console.error(`Exporting Ranking Widget Data`);
      }
    });

    return data;
  };

  getReportWidgetData = ({ locale, reports, widget }) => {
    const {
      dataSets,
      reportTemplateIds,
      widgetSettings: { dataPaths, dataStatistic, range },
    } = widget;

    if (!dataSets) {
      return [];
    }

    const path = dataPaths[0];
    const pathSplit = path.split('.');
    const type = startCase(pathSplit[0]);
    const subType = this.getFormattedPathSubType(pathSplit[1]);

    const data = [];
    const defaultTitles = [
      `${locale.dashboard.current} ${range}`,
      `${locale.dashboard.previous} ${range}`,
    ];
    const reportsData = reportTemplateIds.map((templateId) =>
      this.getReportById({ reports, templateId }),
    );

    dataSets.forEach((dataSet, index) => {
      const report = reportsData[index];
      const title = report ? report.getName() : defaultTitles[index];
      dataSet.forEach((datum) => {
        data.push({
          'data-set': title,
          type,
          'sub-type': subType,
          value: getBucketData({ bucket: datum, dataStatistic, dataPaths }),
          'start-time': moment(datum.startTime, 'x').toISOString(),
          'end-time': moment(datum.endTime, 'x').toISOString(),
        });
      });
    });

    return data;
  };

  getRangeDetailOptionsOfRange = (range, options) =>
    range === WIDGET_INHERIT_VALUE ? options[HISTOGRAM_RANGE.DAY] : options[range];

  getDefaultRangeDetails = (range) =>
    range === WIDGET_INHERIT_VALUE
      ? RANGE_DETAIL_DEFAULT_VALUE_BY_RANGE.Day
      : RANGE_DETAIL_DEFAULT_VALUE_BY_RANGE[range];

  calcTotalsAndDifference = ({
    histogramCustomFieldsDict,
    widget,
    prevIndex = 0,
    currentIndex = 1,
  }) => {
    return calcTotalsAndDifferenceUtil({
      histogramCustomFieldsDict,
      widget,
      prevIndex,
      currentIndex,
    });
  };

  haveAllWidgetsFetched = (widgets) => {
    for (const widget of widgets) {
      const {
        widgetSettings: { type },
      } = widget;
      const isFetchable = !constants.NON_FETCHABLE_WIDGETS.includes(type);
      if (isFetchable && !widget.fetched) {
        return false;
      }
    }
    return true;
  };

  findTickerWidgetIndex = ({ widgets }) =>
    findIndex(widgets, ({ widgetSettings: { type } }) => type === WIDGET_TYPE_EVENT_TICKER);

  getTickerWidgetState = ({ filters, widgets }) => {
    // Currently only support first ticker widget found
    const tickerIndex = this.findTickerWidgetIndex({ widgets });
    const hasTicker = tickerIndex > -1;
    const output = {
      tickerWidget: null,
      tickerWidgetIndex: hasTicker ? tickerIndex : null,
    };
    if (hasTicker) {
      const sourceTickerWidget = widgets[tickerIndex];
      const tickerWidget = this.widgetPreProcessForDisplay({
        filters,
        widget: sourceTickerWidget,
      });
      output.tickerWidget = tickerWidget;
    }
    return output;
  };

  getAllWidgetInfo = ({ allWidgets, widgets }) => {
    const visibleIds = {};
    const output = {
      allWidgetCount: 0,
      hidden: [],
    };
    let hasTicker = false;

    widgets.forEach((widget) => {
      const {
        id,
        widgetSettings: { type },
      } = widget;
      if (type === WIDGET_TYPE_EVENT_TICKER) {
        hasTicker = true;
      }
      visibleIds[id] = true;
    });

    allWidgets.forEach((widget) => {
      const {
        id,
        widgetSettings: { type },
      } = widget;
      if (type !== WIDGET_TYPE_EVENT_TICKER) {
        output.allWidgetCount++;
        if (!visibleIds[id]) {
          output.hidden.push({
            ...widget,
            isHidden: true,
          });
        }
      }
    });
    if (hasTicker) {
      output.allWidgetCount++;
    }
    return output;
  };

  areWidgetListsMatching = ({ allWidgets, widgets }) => {
    const areListsMatching =
      isArray(allWidgets) && isArray(widgets) && allWidgets.length === widgets.length;
    if (!areListsMatching) {
      return false;
    }
    // We're looking for same IDs, in the same order
    for (let index = 0; index < widgets.length; index++) {
      if (allWidgets[index].id !== widgets[index].id) {
        return false;
      }
    }
    return true;
  };

  mergeHiddenWidgets = ({ hiddenWidgets, widgets }) => {
    if (this.areWidgetListsMatching({ allWidgets: hiddenWidgets, widgets })) {
      return widgets;
    }
    const newWidgets = cloneDeep(widgets);
    hiddenWidgets.forEach((widget) => newWidgets.push(widget));
    return newWidgets;
  };

  stripAllHiddenWidgets = ({ widgets }) => widgets.filter((widget) => !widget.isHidden);

  removeHiddenWidget = ({ list, widgetId }) => list.filter((hidden) => hidden.id !== widgetId);

  getWidgetHeaderIcon = (widget) => {
    const icons = [];
    if (this.isWidgetDefaultOrCustomized(widget)) {
      icons.push(<Public key="public-icon" />);
    }
    if (widget.thresholds?.length) {
      const threshold = widget.thresholds[0];
      const { isEmailEnabled, isSMSEnabled } = threshold;
      if (isEmailEnabled || isWidgetInDigest(widget.widgetSettings)) {
        icons.push(<MDIIcon icon={EmailIcon} size={20} key="email-icon" />);
      }
      if (isSMSEnabled) {
        icons.push(<MDIIcon icon={SMSIcon} size={20} key="sms-icon" />);
      }
    }
    return icons.length ? <>{icons}</> : null;
  };
  getCameraGroupId = ({ cameraGroupId, cameraGroups }) => {
    let lookupGroupName = '';
    if (cameraGroupId === constants.CAMERA_GROUP_FAVORITES) {
      lookupGroupName = constants.FAVOURITE_CAMERAS_GROUP_NAME;
    } else if (cameraGroupId === constants.CAMERA_GROUP_RECENT) {
      lookupGroupName = constants.RECENT_CAMERAS_GROUP_NAME;
    }
    return lookupGroupName
      ? get(find(cameraGroups, ['name', lookupGroupName]), 'id')
      : cameraGroupId;
  };
  getCamerasFromGroups = ({ cameraGroupId, cameraGroupName, cameraGroups }) => {
    let camName = cameraGroupName;
    let camId;
    if (cameraGroupId === constants.CAMERA_GROUP_FAVORITES) {
      camName = constants.FAVOURITE_CAMERAS_GROUP_NAME;
    } else if (cameraGroupId === constants.CAMERA_GROUP_RECENT) {
      camName = constants.RECENT_CAMERAS_GROUP_NAME;
    } else {
      camId = cameraGroupId;
    }
    const groupId = camId || get(find(cameraGroups, ['name', camName]), 'id');
    return get(find(cameraGroups, ['id', groupId]), 'cameras', []);
  };

  getDataPathUnitType = ({ histogramCustomFieldsDict, dataPath, isLegacyDataPath = false }) => {
    return CommonWidgetUtils.getDataPathUnitType({
      dataPath,
      histogramCustomFieldsDict,
      isLegacyDataPath,
    });
  };

  getDataPathFromWidget = (widget) => {
    const dataPaths = get(widget, 'widgetSettings.dataPaths');
    return isArray(dataPaths) ? dataPaths[0] : dataPaths;
  };

  getDataPathRatioWithFields = (histogramCustomFieldsDict, dataPath) => {
    return CommonWidgetUtils.getDataPathRatioWithFields({ dataPath, histogramCustomFieldsDict });
  };

  canDataFieldBeAddedToTable = ({ fieldId, tableColumns }) =>
    fieldId &&
    !find(tableColumns, ['id', fieldId]) &&
    !WIDGET_LEGACY_DATA_FIELDS.includes(fieldId) &&
    fieldId !== RANKING_WIDGET_DEFAULT_RANK_BY &&
    fieldId !== RANKING_WIDGET_DEFAULT_RANK_BY_LOCATION;

  // Add the data field (table column) to the event table if it's not already there
  addTableColumns = (fields, preferences, setPreferences) => {
    const tableColumnsSettings = preferences.TABLEVIEW_SETTINGS;
    const newTableColumns = isArray(tableColumnsSettings)
      ? cloneDeep(tableColumnsSettings)
      : mutableDeepClone(TABLEVIEW_STANDARD_FIELDS);

    fields.forEach((field) => {
      if (this.canDataFieldBeAddedToTable({ fieldId: field, tableColumns: newTableColumns })) {
        newTableColumns.push({ id: field });
      }
    });

    setPreferences({ TABLEVIEW_SETTINGS: newTableColumns });
  };

  getInvestigateChartConfig = () => {
    return {
      dataSource: {
        chart: {
          bgAlpha: 3,
          bgColor: '#000000',
          drawAnchors: true,
          interactiveLegend: 0,
          legendIconBorderThickness: 3,
          legendItemFontSize: 13,
          legendPosition: 'right',
          plotHighlightEffect: 0,
          toolTipBgAlpha: 90,
          valueBgAlpha: 40,
          valueBgColor: '#000000',
          valueBorderPadding: 2,
          valueBorderRadius: 3,
          valueFontColor: '#ffffff',
          valueFontSize: 14,
        },
      },
    };
  };

  getGeneralChartConfig = () => {
    return {
      dataSource: {
        chart: {
          chartBottomMargin: 0,
          toolTipBgAlpha: 90,
        },
      },
    };
  };

  /** Extra canvas margins for Add/Edit widget preview — FC clips axis labels at 0. */
  getAspenPreviewChartLayoutConfig = ({ withLeftMargin = false } = {}) => {
    return {
      dataSource: {
        chart: {
          chartBottomMargin: 24,
          chartTopMargin: 6,
          chartRightMargin: 12,
          ...(withLeftMargin ? { chartLeftMargin: 12 } : {}),
        },
      },
    };
  };

  getRankingWidgetChartConfig = ({ eventStandard, widget, filterDate, locale }) => {
    const {
      widgetSettings: { dataStatistic, dataPaths, range, rangeRolling },
    } = widget;
    const summaryView = get(widget, 'widgetSettings.summaryView', this.isWidgetSummaryView(widget));

    const clientSettings = AuthService.getSession()?.clientSettings;
    const firstDayOfWeek = getDayOfWeekNumber(clientSettings?.firstDayOfWeek);

    const { formatted: rangeInfo } = HistogramApiHelper.getDateRangeInfo({
      date: filterDate,
      firstDayOfWeek,
      locale,
      range,
      rangeRolling,
      shortString: true,
      widgetType: WIDGET_TYPE_RANKING,
    });

    const fieldLabel = getRankingWidgetDataFieldName({
      eventStandard,
      fallbackName: locale.fieldName.fieldNameEmpty,
      dataPaths,
      localeCode: locale.locale,
      localizationNameType: dataStatistic,
    });

    return {
      dataSource: {
        chart: {
          chartLeftMargin: summaryView ? 10 : 7,
          yAxisName: getRankingWidgetYAxisName({ fieldLabel, rangeInfo, summaryView }),
          yAxisNameFontSize: 11,
          yAxisNamePadding: 5,
        },
      },
    };
  };

  getBasedOnReportString = ({ reportTemplateIds, locale, reports }) => {
    const localeCode = locale.locale;
    let basedOnReportString = '';

    if (reports && !isEmpty(reportTemplateIds)) {
      const reportNames = reportTemplateIds.map((id) =>
        CommonWidgetUtils.getReportName({ reports, id, localeCode }),
      );
      if (reportNames.length === 1) {
        basedOnReportString = LocaleUtil.getLocalizedStr({
          string: locale.dashboard.basedOnReport,
          replacements: {
            reportName: reportNames[0],
          },
        });
      } else if (reportNames.length === 2) {
        basedOnReportString = LocaleUtil.getLocalizedStr({
          string: locale.dashboard.basedOnReports,
          replacements: {
            firstReportName: reportNames[0],
            secondReportName: reportNames[1],
          },
        });
      }
    }

    return basedOnReportString;
  };

  getGraphType = ({ widget, graphIndex = null }) => {
    const widgetType = get(widget, 'widgetSettings.type');
    const summaryView = get(widget, 'widgetSettings.summaryView', IS_RANKING_DEFAULT_SUMMARY_VIEW);
    const graphTypes = get(widget, 'widgetSettings.graphTypes', []);

    let graphType = GRAPH_TYPE.BAR;
    if (CommonWidgetUtils.isRankingWidget(widgetType) && !summaryView) {
      graphType = GRAPH_TYPE.LINE;
    } else if (graphIndex != null && !isEmpty(graphTypes)) {
      graphType = graphTypes[graphIndex];
    } else {
      graphType = get(widget, 'widgetSettings.graphType', GRAPH_TYPE.BAR);
    }

    return graphType;
  };

  isFetchedDataStale(lastFetchDataParams, fetchDataParams) {
    return !lastFetchDataParams || !isEqual(lastFetchDataParams, fetchDataParams);
  }

  getEventTypes(eventStandard, dataPaths) {
    const { eventTypes, eventSubtypes } = CommonWidgetUtils.getEventTypes({
      dataPaths,
      eventStandard,
    });

    return {
      eventTypes: mapKeys(eventTypes, (value) => value),
      eventSubtypes: mapKeys(eventSubtypes, (value) => value),
    };
  }

  getWidgetDataSetReportId({ datasetIndex, dataIndex, widget }) {
    const {
      reportTemplateIds,
      widgetSettings: { range },
    } = widget;

    const reportIds = filter(reportTemplateIds, (id) => id !== ALL_EVENTS_REPORT);

    // All reports, so return nothing
    if (reportIds.length === 0) {
      return;
    }

    const type = get(widget, 'widgetSettings.type');
    const isReport = CommonWidgetUtils.isReportWidget(type);
    const defaultSummaryView = isReport
      ? CommonWidgetUtils.isReportWidgetDefaultSummaryView(range)
      : IS_RANKING_DEFAULT_SUMMARY_VIEW;
    const summaryView = get(widget, 'widgetSettings.summaryView', defaultSummaryView);

    // Only one report, return that one
    // (Applicable to ReportWidget or RankingWidget)
    if (reportIds.length === 1) {
      return reportIds[0];
    }
    // Multiple reports, return report id corresponding to data set index clicked
    // (Applicable to ReportWidget only)
    else {
      return summaryView ? reportIds[dataIndex] : reportIds[datasetIndex];
    }
  }
}

export default new HomeHelper();
